import {
  BRAND,
  BRAND_SHORT,
  DEFAULT_OG_IMAGE,
  INDEX,
  NOINDEX,
  NOINDEX_NOFOLLOW,
  SITE_ORIGIN,
} from "./config.mjs";
import {
  breadcrumbNode,
  collectionNode,
  graph,
  organizationNode,
  productNode,
  webSiteNode,
} from "./jsonld.mjs";

// Product images arrive from the API as raw Jmix FileRef strings ("s3://..." in
// production, "fs://..." on local storage). Route them through the storefront's
// own /cdn-image rewrite rather than the Railway host directly: social scrapers
// treat cross-origin image hosts inconsistently, and this keeps the backend
// hostname out of every shared link and out of Google's index.
//
// Note this passes the FileRef through whole, unlike src/utils/Timeout.js's
// processImageUrl, which only rewrites the s3:// form.
export const imageUrl = (origin, ref, width = 1200) => {
  if (!ref) return null;
  if (/^https?:\/\//.test(ref)) return ref;
  return `${origin}/cdn-image?ref=${encodeURIComponent(ref)}&w=${width}`;
};

const productImages = (origin, product) =>
  [product.imageUrl, product.image1, product.image2, product.image3]
    .filter(Boolean)
    .map((ref) => imageUrl(origin, ref))
    .filter(Boolean);

// Descriptions read best when cut at a sentence boundary rather than mid-word.
const summarise = (text, max = 155) => {
  if (!text) return null;
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;

  const window = clean.slice(0, max);
  const sentenceEnd = Math.max(
    window.lastIndexOf(". "),
    window.lastIndexOf("! "),
    window.lastIndexOf("? ")
  );
  if (sentenceEnd > max * 0.5) return window.slice(0, sentenceEnd + 1);

  const wordEnd = window.lastIndexOf(" ");
  return `${window.slice(0, wordEnd > 0 ? wordEnd : max).trim()}…`;
};

const money = (value) =>
  value == null ? null : `$${Number(value).toFixed(2)}`;

const HOME_TITLE = `Artisan Perfume, Handcrafted in the USA | ${BRAND}`;
const HOME_DESCRIPTION =
  "Small-batch artisan fragrances, handcrafted in the USA. Explore the Zecado collection and find your signature scent. Worldwide delivery.";

// Static route classes. Anything not listed here and not data-driven falls
// through to `fallback`, which returns a 404.
const STATIC_ROUTES = {
  home: { path: "/", title: HOME_TITLE, description: HOME_DESCRIPTION },
  shop: {
    path: "/shop",
    title: `Shop All Fragrances | ${BRAND}`,
    description:
      "Browse every Zecado fragrance — small-batch perfumes handcrafted in the USA. Find your signature scent, with worldwide delivery.",
  },
  about: {
    path: "/about",
    title: `About Zecado — Artisan Perfumers | ${BRAND}`,
    description:
      "Meet the small-batch American perfume house behind Zecado — our story, our ingredients and the craft behind every bottle.",
  },
  contact: {
    path: "/contact",
    title: `Contact Us | ${BRAND}`,
    description:
      "Questions about an order or about a scent? Reach the Zecado team and we will reply within one business day.",
  },
  privacy: {
    path: "/privacy-policy",
    title: `Privacy Policy | ${BRAND}`,
    description:
      "How Zecado Fragrance collects, uses and protects your personal information.",
  },
  terms: {
    path: "/terms-and-conditions",
    title: `Terms & Conditions | ${BRAND}`,
    description:
      "The terms governing your use of zecadofragrance.com and any purchase made from Zecado Fragrance.",
  },
};

// Transactional and per-customer routes. Matched by prefix so the parameterised
// ones (/orderid/:id, /review/:token) are covered too.
const NOINDEX_PREFIXES = [
  "/cart",
  "/checkout",
  "/return",
  "/orderid",
  "/review",
  "/login",
  "/register",
];

export const robotsForPath = (path) => {
  // /search/:query is an unbounded URL space - the one place nofollow is right,
  // so a crawler never starts generating query permutations.
  if (path.startsWith("/search")) return NOINDEX_NOFOLLOW;
  if (NOINDEX_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return NOINDEX;
  }
  return INDEX;
};

const base = ({ origin, path, title, description, robots, image, imageAlt, ogType }) => ({
  title,
  description,
  canonical: `${origin}${path}`,
  robots,
  ogType: ogType || "website",
  image: image || `${origin}${DEFAULT_OG_IMAGE}`,
  imageAlt: imageAlt || BRAND,
  // Dimensions are declared only for the fixed site-wide fallback, which really
  // is 1200x630. /cdn-image preserves the source aspect ratio, so declaring
  // dimensions for a product image would be a guess - and a wrong guess makes
  // the social preview render incorrectly.
  imageWidth: image ? null : 1200,
  imageHeight: image ? null : 630,
  jsonLd: [],
  status: 200,
  knownRoute: true,
});

/**
 * Pure, isomorphic. Called by api/render.mjs on the server and by src/seo/Seo.jsx
 * in the browser, so both produce identical tags and cannot drift.
 *
 * @param {object} input
 * @param {string} input.type   route class: home|shop|product|about|contact|privacy|terms|notFound|fallback
 * @param {string} input.path   pathname the canonical should point at
 * @param {object} [input.data] fetched payload ({ product, reviews } for a PDP)
 * @param {string} [input.origin]
 */
export const buildMeta = ({ type, path, data = null, origin = SITE_ORIGIN }) => {
  const staticRoute = STATIC_ROUTES[type];

  if (staticRoute) {
    const meta = base({
      origin,
      path: staticRoute.path,
      title: staticRoute.title,
      description: staticRoute.description,
      robots: INDEX,
    });
    meta.jsonLd = [graph([organizationNode(origin), webSiteNode(origin)])];
    return meta;
  }

  if (type === "product" && data?.product) {
    const product = data.product;
    const images = productImages(origin, product);
    const price = product.hasDiscount ? product.discountedPrice : product.price;

    const categoryLabel = product.category?.name
      ? `${product.category.name} Perfume`
      : "Eau de Parfum";
    const title =
      product.metaTitle || `${product.name} — ${categoryLabel} | ${BRAND_SHORT}`;

    // Fall back through: editor-authored meta, then the product description,
    // then a generated line. A product page must never inherit the homepage
    // description - that is what made every PDP look identical to Google.
    const descriptionParts = [
      product.metaDescription ||
        summarise(product.description, 120) ||
        `${product.name}, a small-batch fragrance handcrafted by Zecado.`,
    ];
    const priceLabel = money(price);
    if (priceLabel) descriptionParts.push(`${priceLabel}.`);
    descriptionParts.push("Handcrafted in the USA.");

    const meta = base({
      origin,
      path,
      title,
      description: summarise(descriptionParts.join(" ")),
      robots: INDEX,
      image: images[0],
      imageAlt: `${product.name} by ${BRAND_SHORT}`,
      ogType: "product",
    });

    const trail = [
      { name: "Home", path: "/" },
      { name: "Shop", path: "/shop" },
      ...(product.category?.name ? [{ name: product.category.name }] : []),
      { name: product.name },
    ];

    meta.jsonLd = [
      graph([
        organizationNode(origin),
        webSiteNode(origin),
        breadcrumbNode(origin, path, trail),
        productNode({
          origin,
          path,
          product,
          images,
          reviews: data.reviews || [],
        }),
      ]),
    ];
    return meta;
  }

  // The backend was unreachable, so we cannot describe this page specifically.
  // The one thing that must still be right is the canonical: falling back to the
  // shell's built-in homepage head would point a product URL at "/", which is an
  // instruction to drop that product from the index in favour of the homepage.
  // Generic title, honest self-canonical, no invented product claims.
  if (type === "degraded") {
    const meta = base({
      origin,
      path,
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      robots: robotsForPath(path),
    });
    meta.jsonLd = [graph([organizationNode(origin), webSiteNode(origin)])];
    return meta;
  }

  if (type === "notFound") {
    const meta = base({
      origin,
      path,
      title: `Page Not Found | ${BRAND}`,
      description: "This page no longer exists. Browse the Zecado fragrance collection instead.",
      robots: NOINDEX,
    });
    meta.status = 404;
    meta.knownRoute = false;
    meta.jsonLd = [graph([organizationNode(origin)])];
    return meta;
  }

  // `fallback`: a real SPA route that needs no data (cart, checkout, search,
  // login...) or an unknown URL. robotsForPath decides which, and an unknown
  // path gets a genuine 404 instead of the soft 404 the SPA used to serve.
  const robots = robotsForPath(path);
  const meta = base({
    origin,
    path,
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    robots,
  });
  meta.jsonLd = [graph([organizationNode(origin), webSiteNode(origin)])];

  // Every indexable route is enumerated in STATIC_ROUTES or handled by an
  // explicit type. So a path reaching `fallback` that is not in the noindex set
  // is an unknown URL, and it gets a real 404 rather than the HTTP 200 the SPA
  // used to serve for everything (a soft 404, which Google penalises).
  meta.knownRoute = robots !== INDEX;
  if (!meta.knownRoute) {
    meta.status = 404;
    meta.robots = NOINDEX;
    meta.title = `Page Not Found | ${BRAND}`;
    meta.description = "This page no longer exists. Browse the Zecado fragrance collection instead.";
  }
  return meta;
};

// Maps a pathname to the route class the server would have used. Only the client
// needs this: on the server the class is already pinned by the vercel.json
// rewrite that dispatched the request.
export const routeTypeForPath = (path) => {
  for (const [type, route] of Object.entries(STATIC_ROUTES)) {
    if (route.path === path) return type;
  }
  if (path.startsWith("/product/")) return "product";
  return "fallback";
};

export { collectionNode, summarise };
